import axios from "axios";
import forge from "node-forge";
import { auth } from "@/config/host.config";

export interface IPubKeyResponse {
  enabled: boolean;
  publicKey: string;
  fingerprint: string;
  algorithm: string;
  prefix: string;
  plaintextFallback: boolean;
}

export interface IChallengeResponse {
  enabled: boolean;
  nonce: string;
}

interface ICachedKey {
  fingerprint: string;
  pem: string;
  prefix: string;
  key: forge.pki.rsa.PublicKey;
}

const DEFAULT_PREFIX = "rsa:";

let cache: ICachedKey | null = null;

export async function fetchPubKeyConfig(): Promise<IPubKeyResponse> {
  const res = await axios.get<IPubKeyResponse>(auth.pubkey);
  return res.data;
}

export async function fetchChallenge(): Promise<string | null> {
  try {
    const res = await axios.get<IChallengeResponse>(auth.challenge);
    if (res.data && res.data.enabled) return res.data.nonce ?? null;
    return null;
  } catch (e) {
    console.warn("fetchChallenge: challenge fetch failed", e);
    return null;
  }
}

export function resetKeyCache(): void {
  cache = null;
}

async function getCachedKey(config: IPubKeyResponse): Promise<ICachedKey> {
  if (
    cache &&
    cache.fingerprint === config.fingerprint &&
    cache.pem === config.publicKey
  ) {
    return cache;
  }
  const key = forge.pki.publicKeyFromPem(config.publicKey);
  cache = {
    fingerprint: config.fingerprint,
    pem: config.publicKey,
    prefix: config.prefix || DEFAULT_PREFIX,
    key,
  };
  return cache;
}

function encryptWithRsa(
  value: string,
  nonce: string | null,
  key: forge.pki.rsa.PublicKey
): string {
  const payload = JSON.stringify(nonce ? { v: value, n: nonce } : { v: value });
  const utf8 = forge.util.encodeUtf8(payload);
  const encrypted = key.encrypt(utf8, "RSA-OAEP", {
    md: forge.md.sha256.create(),
    mgf1: {
      md: forge.md.sha256.create(),
    },
  });
  return forge.util.encode64(encrypted);
}

export async function prepareCredentialsFields(
  fields: Record<string, string>,
  nonce: string | null
): Promise<Record<string, string>> {
  let config: IPubKeyResponse;
  try {
    config = await fetchPubKeyConfig();
  } catch (e) {
    console.warn(
      "prepareCredentialsFields: pubkey fetch failed, sending plain",
      e
    );
    return fields;
  }

  if (!config.enabled) {
    return fields;
  }

  try {
    const cached = await getCachedKey(config);
    const prefix = cached.prefix || DEFAULT_PREFIX;
    const encrypted: Record<string, string> = {};
    for (const [field, value] of Object.entries(fields)) {
      encrypted[field] =
        typeof value === "string"
          ? prefix + encryptWithRsa(value, nonce, cached.key)
          : value;
    }
    return encrypted;
  } catch (e) {
    console.error("prepareCredentialsFields: RSA encryption failed", e);
    if (config.plaintextFallback) {
      return fields;
    }
    throw e;
  }
}

export async function prepareCredentials(
  login: string,
  password: string,
  nonce: string | null
): Promise<{ login: string; password: string }> {
  return (await prepareCredentialsFields({ login, password }, nonce)) as {
    login: string;
    password: string;
  };
}
